import { useCallback, useEffect } from 'react'
import config from 'src/config'

export type WatchPingArea = 'auditorium'

interface UseWatchPingProps {
  area?: WatchPingArea
}

const PING_INTERVAL_MS = 15000

/**
 * System B — the independent, append-only watch-time backup.
 *
 * The client blindly pings; the server marks a 30-second square and watch time
 * is COUNT(squares) * 30 at export time. There is no entry event, no exit event
 * and no state on this side: a dropped ping costs at most one square, never a
 * whole session.
 *
 * DO NOT "refactor" this onto src/apis/apiService (axios). The bare fetch() is
 * the entire point of System B: a bug, an interceptor, a global auth handler or
 * any other failure inside System A's transport layer must not be able to blind
 * B. B shares no code path with A beyond the JWT in localStorage.
 *
 * Unlike useTrack (System A), this hook NEVER reloads the page, never reports an
 * error to the user and never retries — every failure is swallowed.
 */
export const useWatchPing = ({ area = 'auditorium' }: UseWatchPingProps = {}) => {
  const sendPing = useCallback(() => {
    // ISOLATION GUARD — do not remove. sendPing() is called synchronously from
    // inside useEffect, so ANY throw here propagates into React's commit phase
    // and unmounts the whole AuditoriumContainer subtree — taking System A's
    // useTrack, and the auditorium page itself, down with it. Both
    // localStorage.getItem (SecurityError when storage is blocked, e.g. Safari
    // with third-party cookies off or an embedded context) and fetch
    // (ReferenceError where unpolyfilled) can throw synchronously. The .catch
    // below only covers the async rejection. System B must never be able to
    // break System A, so every failure mode is swallowed here.
    try {
      // A hidden/backgrounded tab is not watching: no square is earned.
      if (document.visibilityState !== 'visible') return

      const token = localStorage.getItem('jwt_token')
      if (!token) return

      fetch(`${config.apiUrl}/api/room-state`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ area }),
        keepalive: true,
      }).catch(() => {})
    } catch {
      // Intentionally silent: a lost square is the acceptable cost, a crashed
      // auditorium page is not.
    }
  }, [area])

  useEffect(() => {
    // Fire immediately so a drop-in registers a square without waiting.
    sendPing()

    const handleVisibilityChange = () => {
      // Firing on the hidden -> visible transition is what makes a short
      // drop-in still land inside a bucket.
      if (document.visibilityState === 'visible') {
        sendPing()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    const pingInterval = setInterval(sendPing, PING_INTERVAL_MS)

    // No exit beacon and no teardown request: B intentionally has no exit path.
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearInterval(pingInterval)
    }
  }, [sendPing])
}
